import { z } from 'zod';
import { errorCodeSchema } from './errors.js';
const isoDate = z.iso.datetime({ offset: true });
/** Secret d'invitation lu dans le fragment d'URL (#k=…) : 256 bits en base64url. */
export const invitationSecretSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const guestSessionRequestSchema = z.object({ secret: invitationSecretSchema });
export const displayNameSchema = z
    .string()
    .trim()
    .min(1, 'Indiquez votre nom.')
    .max(60, '60 caractères maximum.')
    .refine((value) => !/[<>]/.test(value), 'Caractères < et > non autorisés.');
export const admissionRequestSchema = z.object({ displayName: displayNameSchema });
/**
 * État de la demande vu par l'invité. NONE : aucune demande active (première visite, demande
 * annulée ou départ volontaire) ; l'invité peut demander à rejoindre.
 */
export const GUEST_ADMISSION_STATUSES = [
    'NONE',
    'PENDING',
    'ADMITTED',
    'DENIED',
    'REMOVED',
];
export const guestAdmissionStatusSchema = z.enum(GUEST_ADMISSION_STATUSES);
export const guestStateSchema = z.object({
    meeting: z.object({
        title: z.string(),
        scheduledStart: isoDate.nullable(),
        scheduledEnd: isoDate.nullable(),
        chatEnabled: z.boolean(),
        screenShareEnabled: z.boolean(),
    }),
    invitation: z.object({ label: z.string() }),
    displayName: z.string().nullable(),
    admission: guestAdmissionStatusSchema,
    /** Un créateur ou co-hôte est connecté (écran de détail ou salle). */
    hostPresent: z.boolean(),
    sessionExpiresAt: isoDate,
});
/**
 * Événements SSE de l'invité : l'état complet à chaque changement, puis `closed` avec la
 * raison quand l'accès prend fin (révocation, fin de réunion, expiration).
 */
export const guestEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('state'), state: guestStateSchema }),
    z.object({ type: z.literal('closed'), reason: errorCodeSchema }),
    z.object({ type: z.literal('ping') }),
]);
export const waitingEntrySchema = z.object({
    requestId: z.uuid(),
    displayName: z.string(),
    invitationLabel: z.string(),
    requestedAt: isoDate,
});
export const admittedEntrySchema = z.object({
    requestId: z.uuid(),
    invitationId: z.uuid(),
    displayName: z.string(),
    invitationLabel: z.string(),
    admittedAt: isoDate,
});
export const waitingRoomSchema = z.object({
    waiting: z.array(waitingEntrySchema),
    admitted: z.array(admittedEntrySchema),
    /** État courant de la réunion : fin pour tous, transfert de propriété. */
    meeting: z.object({
        status: z.enum(['SCHEDULED', 'LIVE', 'ENDED']),
        ownerId: z.uuid(),
    }),
});
/** Événements SSE de l'hôte : file d'attente et invités admis, à chaque changement. */
export const hostEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('waiting-room'), room: waitingRoomSchema }),
    z.object({ type: z.literal('ping') }),
]);
export const hostLeaveRequestSchema = z.object({
    /** Terminer la visio pour tous plutôt que la quitter. */
    endForAll: z.boolean().default(false),
});
export const hostLeaveResponseSchema = z.object({
    outcome: z.enum(['left', 'transferred', 'ended']),
});
//# sourceMappingURL=guest.js.map